import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
} from "@cmmd-forge/contracts";
import type { AssignmentOperation } from "./projectTaskAssignmentSelection";

type ActiveAgent = CmmdBoardTask["activeAgents"][number];

/**
 * The optimistic board patch for one assignment write.
 *
 * `replace` is the historical single-subject behaviour and stays byte-for-byte
 * what it was. `add` and `remove` are scoped: they move exactly one subject and
 * leave every other assignee on the task alone, which is the whole point of
 * multi-assign — a sweep here would delete agents the user never touched.
 */
export function assignmentPatch(input: {
  readonly task: CmmdBoardTask | undefined;
  readonly subject: CmmdProjectTaskAssignmentSubject;
  readonly operation: AssignmentOperation;
  readonly members: readonly CmmdMember[];
  readonly agentMembers: readonly CmmdProjectAgentMember[];
}): Partial<CmmdBoardTask> {
  const { task, subject, operation, members, agentMembers } = input;

  if (operation === "replace") return replacePatch(subject, members, agentMembers);
  if (task === undefined) return {};
  if (subject.kind === "unassigned") return {};

  return operation === "add"
    ? addPatch(task, subject, members, agentMembers)
    : removePatch(task, subject);
}

const CLEARED = {
  assignment: null,
  assignee: null,
  assigneeName: null,
  assigneeAvatar: null,
} as const satisfies Partial<CmmdBoardTask>;

function replacePatch(
  subject: CmmdProjectTaskAssignmentSubject,
  members: readonly CmmdMember[],
  agentMembers: readonly CmmdProjectAgentMember[],
): Partial<CmmdBoardTask> {
  if (subject.kind === "human_user") return humanFields(subject.userId, members);
  if (subject.kind === "agent_member") {
    const agent = toActiveAgent(subject.agentMemberId, agentMembers);
    return {
      ...CLEARED,
      assignment: agent ? { kind: "agent_member", ...agent } : null,
      assigneeName: agent?.displayName ?? null,
    };
  }
  return CLEARED;
}

/** The denormalized assignee columns for a human, as the board renders them. */
function humanFields(
  userId: number,
  members: readonly CmmdMember[],
): Required<Pick<CmmdBoardTask, "assignment" | "assignee" | "assigneeName" | "assigneeAvatar">> {
  const member = members.find((candidate) => Number(candidate.id) === userId);
  return {
    assignment: { kind: "human_user", userId },
    assignee: userId,
    assigneeName: member?.name ?? null,
    assigneeAvatar: member?.avatarUrl ?? null,
  };
}

function addPatch(
  task: CmmdBoardTask,
  subject: Extract<CmmdProjectTaskAssignmentSubject, { kind: "human_user" | "agent_member" }>,
  members: readonly CmmdMember[],
  agentMembers: readonly CmmdProjectAgentMember[],
): Partial<CmmdBoardTask> {
  if (subject.kind === "human_user") {
    return {
      ...humanFields(subject.userId, members),
      // The human takes the primary slot only when no agent already holds it,
      // so adding a reviewer does not quietly demote the working agent.
      ...(task.assignment?.kind === "agent_member" ? { assignment: task.assignment } : {}),
    };
  }

  const agent = toActiveAgent(subject.agentMemberId, agentMembers);
  if (agent === null) return {};
  if (task.activeAgents.some((entry) => entry.agentMemberId === agent.agentMemberId)) return {};

  return {
    activeAgents: [...task.activeAgents, agent],
    assignment: task.assignment ?? { kind: "agent_member", ...agent },
  };
}

function removePatch(
  task: CmmdBoardTask,
  subject: Extract<CmmdProjectTaskAssignmentSubject, { kind: "human_user" | "agent_member" }>,
): Partial<CmmdBoardTask> {
  if (subject.kind === "human_user") {
    return {
      ...CLEARED,
      assignment: task.assignment?.kind === "human_user" ? null : (task.assignment ?? null),
    };
  }

  const remaining = task.activeAgents.filter(
    (entry) => entry.agentMemberId !== subject.agentMemberId,
  );
  const primaryWasRemoved =
    task.assignment?.kind === "agent_member" &&
    task.assignment.agentMemberId === subject.agentMemberId;

  return primaryWasRemoved
    ? { activeAgents: remaining, assignment: promotedPrimary(task, remaining) }
    : { activeAgents: remaining };
}

/**
 * Leaving `assignment` on a subject the user just removed would render a chip
 * they deleted, so promote the next surviving agent — or fall back to the
 * human, or to nothing.
 */
function promotedPrimary(
  task: CmmdBoardTask,
  remaining: readonly ActiveAgent[],
): CmmdBoardTask["assignment"] {
  const promoted = remaining[0];
  if (promoted) return { kind: "agent_member", ...promoted };
  return task.assignee === null ? null : { kind: "human_user", userId: task.assignee };
}

function toActiveAgent(
  agentMemberId: number,
  agentMembers: readonly CmmdProjectAgentMember[],
): ActiveAgent | null {
  const member = agentMembers.find((candidate) => candidate.id === agentMemberId);
  return member
    ? {
        agentMemberId: member.id,
        agentId: member.agentId,
        displayName: member.displayName,
        agentKind: member.agentKind,
        providerKey: member.providerKey,
      }
    : null;
}

/**
 * Did the canonical board confirm this write? Used only after an ambiguous
 * failure, where the request may have landed before the response was lost.
 *
 * `add` and `remove` assert on presence anywhere on the task, not on the
 * primary `assignment` slot: the server is free to keep a different subject
 * primary, and matching only that would read a successful add as a failure.
 */
export function assignmentConfirmed(input: {
  readonly task: CmmdBoardTask | undefined;
  readonly subject: CmmdProjectTaskAssignmentSubject;
  readonly operation: AssignmentOperation;
}): boolean {
  const { task, subject, operation } = input;
  if (!task) return false;

  if (operation === "replace") {
    if (subject.kind === "unassigned") return task.assignment === null;
    if (subject.kind === "human_user") {
      return task.assignment?.kind === "human_user" && task.assignment.userId === subject.userId;
    }
    return (
      task.assignment?.kind === "agent_member" &&
      task.assignment.agentMemberId === subject.agentMemberId
    );
  }

  if (subject.kind === "unassigned") return task.assignment === null;
  const present = subjectPresent(task, subject);
  return operation === "add" ? present : !present;
}

function subjectPresent(
  task: CmmdBoardTask,
  subject: Extract<CmmdProjectTaskAssignmentSubject, { kind: "human_user" | "agent_member" }>,
): boolean {
  if (subject.kind === "human_user") {
    return (
      task.assignee === subject.userId ||
      (task.assignment?.kind === "human_user" && task.assignment.userId === subject.userId)
    );
  }
  return (
    task.activeAgents.some((entry) => entry.agentMemberId === subject.agentMemberId) ||
    (task.assignment?.kind === "agent_member" &&
      task.assignment.agentMemberId === subject.agentMemberId)
  );
}
